export const restorationStages = [
  { code: 'arrival', title: 'Chegada & recepção' },
  { code: 'assessment', title: 'Avaliação técnica' },
  { code: 'disassembly', title: 'Desmontagem' },
  { code: 'bodywork', title: 'Funilaria' },
  { code: 'paint', title: 'Pintura' },
  { code: 'mechanics', title: 'Mecânica' },
  { code: 'assembly', title: 'Montagem' },
  { code: 'final-review', title: 'Revisão final' },
  { code: 'delivery', title: 'Entrega' }
] as const

export interface SequencedStage {
  sequence: number
  status?: string | null
}

export function sortStagesBySequence<T extends { sequence: number }>(stages: readonly T[]): T[] {
  return [...stages].sort((a, b) => a.sequence - b.sequence)
}

export function isCompletedStageStatus(status: string | null | undefined): boolean {
  return ['completed', 'complete', 'done', 'finished'].includes((status ?? '').toLowerCase())
}

export function findResumeStage<T extends SequencedStage>(stages: readonly T[]): T | undefined {
  return sortStagesBySequence(stages).find((stage) => !isCompletedStageStatus(stage.status))
}

export const DATE_INPUT_MAX_LENGTH = 10
export const MAX_BRL_DIGITS = 12
export const MAX_INTEGER_INPUT_DIGITS = 6
export const MAX_DECIMAL_INPUT_DIGITS = 6

export { digitsOnly, formatCep, formatCpfCnpj, formatPhone, normalizeCep, normalizeCpfCnpj, normalizePhone } from './customer-fields'

const brlFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })

export function formatBrl(value: number): string {
  return brlFormatter.format(Number.isFinite(value) ? value : 0).replace(/\u00a0/g, ' ')
}

export function formatBrlInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, MAX_BRL_DIGITS)
  if (!digits) return ''
  return formatBrl(Number(digits) / 100)
}

export function parseBrl(value: string): number | null {
  const digits = value.replace(/\D/g, '').slice(0, MAX_BRL_DIGITS)
  if (!digits) return null
  const parsed = Number(digits) / 100
  return Number.isFinite(parsed) ? parsed : null
}

export function formatIntegerInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, MAX_INTEGER_INPUT_DIGITS)
  if (!digits) return ''
  return digits.replace(/^0+(?=\d)/, '')
}

export function parseIntegerInput(value: string): number | null {
  const formatted = formatIntegerInput(value)
  if (!formatted) return null
  const parsed = Number(formatted)
  return Number.isInteger(parsed) ? parsed : null
}

export function formatDecimalInput(value: string): string {
  const raw = value.replace(/\s/g, '').replace(',', '.')
  const separatorIndex = raw.indexOf('.')
  const integerPart = (separatorIndex === -1 ? raw : raw.slice(0, separatorIndex))
    .replace(/\D/g, '')
    .slice(0, MAX_DECIMAL_INPUT_DIGITS)
  const fractionPart = separatorIndex === -1
    ? ''
    : raw.slice(separatorIndex + 1).replace(/\D/g, '').slice(0, 2)
  const integer = integerPart.replace(/^0+(?=\d)/, '')
  if (!integer && !fractionPart && !/[.,]$/.test(value)) return ''
  if (/[.,]$/.test(value)) return `${integer || '0'},`
  return `${integer || '0'}${fractionPart ? `,${fractionPart}` : ''}`
}

export function parseDecimalInput(value: string): number | null {
  const formatted = formatDecimalInput(value).replace(',', '.')
  if (!formatted || formatted.endsWith('.')) return null
  const parsed = Number(formatted)
  return Number.isFinite(parsed) ? parsed : null
}

export function formatDateInput(value: string): string {
  const trimmed = value.trim()
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed)
  if (isoMatch) return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`

  const digits = trimmed.replace(/\D/g, '').slice(0, 8)
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

export function parseDateInput(value: string): string | null {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value)
  if (!match) return null
  const day = Number(match[1])
  const month = Number(match[2])
  const year = Number(match[3])
  const date = new Date(Date.UTC(year, month - 1, day))
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null
  return date.toISOString()
}

export function formatDateBr(iso: string | null | undefined): string {
  if (!iso) return '—'
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (match) return `${match[3]}/${match[2]}/${match[1]}`
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('pt-BR').format(date)
}

export function formatDateTimeBr(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

export type ChecklistStatus = 'pending' | 'ok' | 'attention' | 'rejected' | 'notApplicable'
export type UserRole = 'admin' | 'manager' | 'technician' | 'customer'
export type MediaPhase = 'before' | 'during' | 'after'

export interface VehicleSummary {
  id: string
  brand: string
  model: string
  year: number
  plate: string
}

export interface VehicleDetail extends VehicleSummary {
  vin: string | null
  color: string | null
  customer: { id: string; name: string; email: string; phone: string | null }
}

export interface WorkOrderSummary {
  id: string
  number: string
  status: string
  customer: string
  currentStage: string | null
  currentStageSequence: number | null
  stageCount: number
  progress: number
  rejectedItemCount: number
  coverMediaId: string | null
  expectedDelivery: string | null
  vehicle: VehicleSummary
}

export interface ChecklistItemResult {
  id: string
  group: string
  title: string
  status: ChecklistStatus
  note: string | null
  capturedAt: string | null
  version: number
}

export interface ChecklistStageResult {
  id: string
  code: string
  title: string
  sequence: number
  status: string
  progress: number
  completedAt: string | null
  version: number
  items: ChecklistItemResult[]
}

export interface MediaAssetSummary {
  id: string
  fileName: string
  contentType: string
  size: number
  sha256: string
  checklistItemResultId: string | null
  visibleToCustomer: boolean
  phase: MediaPhase
  kind: 'cover' | 'attachment'
  createdAt: string
}

export interface PartLine {
  id: string
  code: string
  description: string
  quantity: number
  unitPrice: number
}

export interface LaborEntry {
  id: string
  description: string
  hours: number
  hourlyRate: number
  technicianSubject: string | null
}

export interface TimelineEventSummary {
  id: string
  eventType: string
  description: string
  actorSubject: string | null
  visibleToCustomer: boolean
  occurredAt: string
}

// Corpo completo de GET /work-orders/{id} — inclui etapas com itens, peças, mão de obra e
// linha do tempo, usado pelas telas de detalhe (Veículo/Checklist no app do mecânico, OS no
// Operacional, Acompanhamento/Relatório no Portal do Cliente).
export interface WorkOrderDetail {
  id: string
  number: string
  status: string
  vehicleId: string
  vehicle: VehicleDetail
  assignedTechnicianSubject: string | null
  technicianDisplayName: string | null
  coverMediaId: string | null
  expectedDelivery: string | null
  isPublished: boolean
  version: number
  createdAt: string
  stages: ChecklistStageResult[]
  parts: PartLine[]
  labor: LaborEntry[]
  timeline: TimelineEventSummary[]
}

export interface ReportSummary {
  id: string
  revision: number
  variant: ReportVariant
  status: string
  total: number
  requestedAt: string
  generatedAt: string | null
}

export interface ReportApprovalSummary {
  id: string
  subject: string
  documentSha256: string
  ipAddress: string
  userAgent: string
  acceptedAt: string
}

export interface ReportDetail extends ReportSummary {
  workOrderId: string
  objectKey: string | null
  sha256: string | null
  approvals: ReportApprovalSummary[]
}

export interface ReportListItem {
  id: string
  workOrderId: string
  workOrderNumber: string
  vehicleBrand: string
  vehicleModel: string
  revision: number
  variant: ReportVariant
  status: string
  total: number
  requestedAt: string
  generatedAt: string | null
}

export type RepairStatus = 'draft' | 'pendingApproval' | 'approved' | 'rejected'
export type QuoteLineKind = 'part' | 'labor'
export type ReportVariant = 'summary' | 'full' | 'quote'

export interface RepairQuoteLine {
  id: string
  kind: QuoteLineKind
  description: string
  quantity: number
  unitPrice: number
  inventoryItemId?: string | null
  inventorySnapshot?: InventoryItemSnapshot | null
}

export interface InventoryItem {
  id: string
  code: string
  description: string
  unitPrice: number
  availableQuantity?: number
  active?: boolean
  version?: number
}

export interface InventoryItemSnapshot {
  id: string
  code: string
  description: string
  unitPrice: number
}

// Reparo proposto pelo mecânico numa etapa do checklist, com orçamento e decisão do cliente.
// Ver ADR-0011. `total` é a soma das linhas (quantity * unitPrice), projetada pela API.
export interface Repair {
  id: string
  workOrderId: string
  stageExecutionId: string
  title: string
  description: string | null
  status: RepairStatus
  customerDecisionNote: string | null
  submittedForApprovalAt: string | null
  decidedAt: string | null
  version: number
  quoteLines: RepairQuoteLine[]
  total: number
}
